import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationTarget } from './notification-target';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { localiseDigits } from '@bookingdei/i18n/format-number';
import { formatTimeAgo } from '@bookingdei/i18n/format-date';
import { api } from '../api';
import { useLanguage } from './language-context';
import { useSession } from './session-context';
import { useLayer } from './layer-stack';
import { NavIcon } from './nav-icon';
import { notificationKind } from './notification-kind';
import type { NotificationItem } from '../mock/seeds/schema-types';

// The merchant notification centre (design M:248-300 + notifVals() M:8355-8390;
// CB-17 `notification-tray.tsx`). Reads the merchant feed GET /me/notifications
// ?scope=merchant (API-68 — there is no /merchant/notifications op); All / Unread tabs
// with counts, "Mark all read" (POST /me/notifications/read with no ids), per-row
// dismiss (POST /me/notifications/{id}/dismiss, API-69), a row opens its `action_url`
// and marks it read, empty states, and "Notification settings" → Account. Mutations
// are optimistic; a failed write re-reads the feed. Registers as a popover layer so
// Esc / Back close it first.
type Tab = 'all' | 'unread';

export function NotificationTray({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { lang, t } = useLanguage();
  const { user } = useSession();
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState<Tab>('all');
  const [nonce, setNonce] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useLayer(open, { kind: 'popover', onDismiss: () => onOpenChange(false), containerRef: panelRef });

  // Bumped by every local mutation: a read that started before a mark-read / dismiss
  // must not overwrite the optimistic state with the pre-mutation feed.
  const mutations = useRef(0);
  const userId = user?.id;

  // Load on mount (for the bell count) and again whenever the tray opens.
  useEffect(() => {
    if (!userId) return;
    const controller = new AbortController();
    const startedAt = mutations.current;
    api
      .GET('/me/notifications', {
        signal: controller.signal,
        params: { query: { scope: 'merchant' } },
      })
      .then((res) => {
        if (mutations.current !== startedAt) return;
        setItems(res.items ?? []);
        setFailed(false);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => controller.abort();
  }, [userId, open, nonce]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) onOpenChange(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onOpenChange]);

  const shown = items.filter((n) => !n.dismissed_at);
  const unread = shown.filter((n) => !n.read_at);
  const rows = tab === 'unread' ? unread : shown;
  const now = new Date();
  const refetch = () => setNonce((n) => n + 1);

  const markRead = (ids?: string[]) => {
    mutations.current += 1;
    const at = now.toISOString();
    setItems((prev) =>
      prev.map((n) => (!ids || ids.includes(n.id) ? { ...n, read_at: n.read_at ?? at } : n)),
    );
    api.POST('/me/notifications/read', { body: ids ? { ids } : {} }).catch(refetch);
  };

  const dismiss = (id: string) => {
    mutations.current += 1;
    const at = now.toISOString();
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, dismissed_at: at } : n)));
    api.POST('/me/notifications/{id}/dismiss', { params: { path: { id } } }).catch(refetch);
  };

  const openRow = (n: NotificationItem) => {
    if (!n.read_at) markRead([n.id]);
    onOpenChange(false);
    const target = notificationTarget(n.entity_ref, n.action_url);
    if (target) navigate(target);
  };

  const body = (n: NotificationItem) => (lang === 'bn' && n.body_bn ? n.body_bn : n.body_en);

  return (
    <div className="notif" ref={wrapRef}>
      <button
        type="button"
        className={`top-bar__icon-btn${open ? ' is-open' : ''}`}
        aria-label={
          unread.length > 0
            ? `${t('dc70.top.notifications')} · ${formatMessage(lang, 'dc70.notif.unread_count', { n: unread.length })}`
            : t('dc70.top.notifications')
        }
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => onOpenChange(!open)}
        data-shell="bell"
      >
        <NavIcon name="bell" size={20} />
        {unread.length > 0 && (
          <span className="top-bar__dot" aria-hidden="true">
            {localiseDigits(lang, unread.length)}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          className="notif__panel"
          role="dialog"
          aria-label={t('dc70.top.notifications')}
          tabIndex={-1}
        >
          <div className="notif__head">
            <span className="notif__title">{t('dc70.top.notifications')}</span>
            {unread.length > 0 && (
              <span className="notif__count">{localiseDigits(lang, unread.length)}</span>
            )}
            <span className="notif__spacer" />
            <button
              type="button"
              className="notif__mark"
              disabled={unread.length === 0}
              onClick={() => markRead()}
            >
              {t('dc70.notif.mark_all')}
            </button>
          </div>
          <div className="notif__tabs" role="tablist" aria-label={t('dc70.top.notifications')}>
            {(['all', 'unread'] as const).map((id) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                className={`notif__tab${tab === id ? ' is-active' : ''}`}
                onClick={() => setTab(id)}
              >
                {formatMessage(lang, id === 'all' ? 'dc70.notif.all' : 'dc70.notif.unread', {
                  n: id === 'all' ? shown.length : unread.length,
                })}
              </button>
            ))}
          </div>
          <div className="notif__list">
            {failed ? (
              <p className="notif__error" role="alert">
                {t('dc70.notif.error')}
              </p>
            ) : rows.length === 0 ? (
              <div className="notif__empty">
                <span className="notif__empty-icon">
                  <NavIcon name="checkCircle" size={19} />
                </span>
                <span className="notif__empty-title">
                  {t(shown.length > 0 ? 'dc70.notif.empty_caught_up' : 'dc70.notif.empty_none')}
                </span>
                <span className="notif__empty-body">{t('dc70.notif.empty_body')}</span>
              </div>
            ) : (
              <ul className="notif__rows">
                {rows.map((n) => {
                  const k = notificationKind(n.kind);
                  const isNew = !n.read_at;
                  return (
                    <li key={n.id} className={`notif__row${isNew ? ' is-new' : ''}`}>
                      <button type="button" className="notif__open" onClick={() => openRow(n)}>
                        <span className={`notif__tile notif__tile--${k.tone}`}>
                          <NavIcon name={k.icon} size={17} strokeWidth={1.9} />
                        </span>
                        <span className="notif__text">
                          <span className="notif__body">{body(n)}</span>
                          <span className="notif__meta">
                            <span>{formatTimeAgo(lang, new Date(n.created_at), now)}</span>
                            {n.action_url && (
                              <>
                                <span className="notif__sep" aria-hidden="true" />
                                <span className="notif__action">{t('dc70.notif.open')}</span>
                              </>
                            )}
                          </span>
                        </span>
                      </button>
                      <span className="notif__dot" aria-hidden="true" />
                      <button
                        type="button"
                        className="notif__dismiss"
                        aria-label={t('dc70.notif.dismiss')}
                        onClick={() => dismiss(n.id)}
                      >
                        <NavIcon name="close" size={13} strokeWidth={2.2} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <button
            type="button"
            className="notif__settings"
            onClick={() => {
              onOpenChange(false);
              navigate('/account');
            }}
          >
            <NavIcon name="gear" size={15} strokeWidth={1.9} />
            <span>{t('dc70.notif.settings')}</span>
          </button>
        </div>
      )}
    </div>
  );
}
